/**
 * Cube `ResultSet` -> per-widget-type data payload.
 *
 * ## Why this exists (TV1-1929 · S14)
 *
 * `getWidgetData` used to do exactly one thing: `return resultSet.rawData()`.
 * That is fine for the six chart types (they want a flat
 * `Record<string, string|number>[]`), but it **throws away the entire ResultSet
 * before any widget sees it** — `tablePivot()`, `tableColumns()`, `totalRow()`
 * and the whole `TableColumn` annotation (`title` / `shortTitle` / `type` /
 * `format` / `currency` / `granularity`) are gone. A table widget cannot be
 * built on `rawData()` without re-deriving the columns from
 * `Object.keys(rows[0])`, which reimplements `tablePivot` AND loses the
 * annotation that drives all the cell formatting.
 *
 * So the fetch path branches HERE, on the widget type, and nowhere else. The
 * six chart parsers are untouched: anything that is not a `table` still gets
 * byte-for-byte the old `rawData()`.
 *
 * ## Extending this (read before you add a branch)
 *
 * This is the ONE seam where a widget type may claim a richer projection of the
 * ResultSet. It deliberately takes `( definition, cubeQuery, resultSet )` — the
 * *query* is passed as well as the result because some projections can only be
 * decided from the query (e.g. a `compareDateRange` time dimension, where
 * `rawData()` throws and you must `resultSet.decompose()`; Cube keeps
 * `queryType` private on the ResultSet). Add a branch, do not add a call site:
 * `WidgetLayoutComponent` should never need to change again.
 *
 * ## TV1-2291 (§6) — this file also owns the READMODEL fetch
 *
 * Cube is no longer the only source. A readmodel-backed widget (`count` / `alert` /
 * `list` / `progress`) never reaches `cubeApi` at all, so its round trip cannot be a
 * projection of a `ResultSet` — it is a query of its own. It lives here anyway,
 * beside `extractWidgetData`, for the reason the docblock above gives: the fetch
 * path branches in this module and `WidgetLayoutComponent.getWidgetData` stays a
 * chokepoint that dispatches rather than a place where query code accumulates.
 *
 * The grammar, the per-collection catalog and the planner are ticket 22's
 * (`readmodelSource.ts`, pure, no I/O). What is added here is only the EXECUTION:
 * take a plan, call the generated client library, unwrap, reduce.
 */

import type { Query, ResultSet, PivotConfig, TableColumn } from '@cubejs-client/core';
import type { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import type { WidgetDefinition } from './WidgetBuilder/types';
import { isDatalessWidgetType, isReadmodelWidgetType } from './types';
import {
  applyPlanToRows,
  isReadmodelFilter,
  planReadmodelQuery,
  readReadmodelCount,
  unwrapReadmodelResponse,
  type ReadmodelFilter,
  type ReadmodelQuery,
  type ReadmodelQueryPlan,
  type ReadmodelRow
} from './readmodelSource';

/**
 * TV1-1930 — what a DATALESS widget (`text`) gets instead of Cube data.
 *
 * ⚠️ It must be TRUTHY. `WebWidget.tsx` opens with `if ( !data ) return
 * <Skeleton/>`, so returning `null` (or `undefined`, or `0`, or `''`) for a
 * dataless widget would render a **permanent skeleton** — a spinner that never
 * resolves, for a widget that was never waiting on anything. An empty object is
 * sufficient, and is deliberately explicit rather than incidental.
 */
export const DATALESS_WIDGET_DATA: Record<string, never> = {};

/**
 * PREFACTOR-grammar — the outcome of fetching ONE widget's data.
 *
 * ## The defect this replaces
 *
 * `getWidgetData` used to `catch` -> `console.error` -> `return null`, and the
 * fetch loop then simply did not write `null` into the map. `WebWidget`'s only
 * guard is `if ( !data ) return <Skeleton/>`. So a Cube query that 400s — a typo'd
 * measure, a cube that does not exist, an expired session, a genuinely broken
 * widget — is INDISTINGUISHABLE from one that is still loading, and the widget
 * spins forever. Nobody sees the console.
 *
 * Three states must be three visibly different things, or the widget is not
 * trustworthy:
 *
 *  - **loading**  — no entry in the map yet -> `<Skeleton/>`;
 *  - **ok, empty** — `status: 'ok'` with zero rows -> the widget's own empty state
 *    (a KPI renders "—", never "0" — see `kpiData.ts`);
 *  - **error**    — `status: 'error'` -> an error tile that says so.
 *
 * A discriminated union rather than `{ data, error? }` so a renderer CANNOT read
 * `data` off a failed fetch without first handling the failure.
 */
export type WidgetDataState =
  | { status: 'ok'; data: unknown }
  | { status: 'error'; error: string }
  | { status: 'incomplete'; reasons: string[] };

export const isWidgetDataError = (
  state: WidgetDataState | undefined
): state is { status: 'error'; error: string } => state?.status === 'error';

/**
 * `incomplete` — the card has not been finished yet. NOT an error (2026-08-17).
 *
 * The fourth state, and the one the union was missing. A card added from the
 * palette is blank by construction, and the builder fills it in one field at a
 * time, so "this definition does not describe a query yet" is the NORMAL condition
 * of a card for as long as someone is configuring it — not a failure.
 *
 * Collapsing it into `error` is what made adding a widget read as "adding widgets
 * fires errors": the card announced **"Widget failed to load — Invalid widget
 * definition: name is required; cube (data source) is required; measures must
 * contain at least one measure"**, in red, from the moment it appeared until the
 * last field was filled. Nothing had failed; the panel beside it was asking for
 * exactly those fields.
 *
 * The split is made at the SOURCE rather than guessed at render time: only
 * `parseWidgetDefinition` throwing produces this state, and it throws for exactly
 * one reason — the definition is not complete enough to build a query from. Every
 * other throw on the path (an unknown Cube member, a 401, a readmodel HTTP failure)
 * is a genuine failure and stays `error`, with the message that names the cause.
 *
 * `reasons` is that validator's own field list, already user-facing and already
 * translated; the tile shows it so the card says what it is still waiting for.
 */
export const isWidgetDataIncomplete = (
  state: WidgetDataState | undefined
): state is { status: 'incomplete'; reasons: string[] } => state?.status === 'incomplete';

/**
 * `true` when this widget has NO Cube query to run.
 *
 * Read by `WidgetLayoutComponent.getWidgetData`, which must skip the round trip
 * entirely: a `text` widget has no `cube` and no `measures`, so
 * `widgetDefinitionToCubeQuery` would produce `{}` and `cubeApi.load({})` would
 * error — an error that `getWidgetData` SWALLOWS into `return null`, i.e. straight
 * back to the permanent skeleton above.
 */
export const isDatalessWidget = ( definition: Pick<WidgetDefinition, 'type'> ): boolean =>
  isDatalessWidgetType( definition?.type );

/**
 * TV1-2291 (§6) — `true` when this widget's data comes from a READMODEL, not Cube.
 *
 * Read by `WidgetLayoutComponent.getWidgetData`, which must branch **before**
 * `parseWidgetDefinition`: a readmodel widget has no `cube` and no `measures`, so
 * the parser rejects it out of hand (`cube (data source) is required`) — and even
 * if it did not, `widgetDefinitionToCubeQuery` would hand back `{}` and
 * `cubeApi.load({})` would error into the error tile. Same shape as
 * {@link isDatalessWidget}, same reason.
 */
export const isReadmodelWidget = ( definition: Pick<WidgetDefinition, 'type'> ): boolean =>
  isReadmodelWidgetType( definition?.type );

/** A single row as produced by `resultSet.tablePivot()` — keyed by column `dataIndex`. */
export type TableWidgetRow = Record<string, string | number | boolean>;

/** A flat Cube row, as produced by `resultSet.rawData()`. */
export type CubeRow = Record<string, string | number | boolean | null>;

/**
 * TV1-1930 — what a `compareDateRange` query yields instead of `rawData()`.
 *
 * `prior` is `null`, NOT `[]`, when the prior window returned nothing: an empty
 * array would let a consumer sum it to `0` and report a confident "+100%" for a
 * period it has no data about. Unknown is not zero (see
 * `marketplace-health/CONTEXT.md:34`).
 */
export type CompareWidgetData = {
  current: CubeRow[];
  prior: CubeRow[] | null;
};

/** `true` for the payload shape produced by the compare branch below. */
export const isCompareWidgetData = ( data: unknown ): data is CompareWidgetData =>
  typeof data === 'object' &&
  data !== null &&
  Array.isArray(( data as CompareWidgetData ).current ) &&
  (( data as CompareWidgetData ).prior === null || Array.isArray(( data as CompareWidgetData ).prior ));

/**
 * Is this a `compareDateRange` query?
 *
 * ⚠️ Decided from the **QUERY**, never from the ResultSet. Cube keeps
 * `queryType` PRIVATE on `ResultSet`, and the alternative — calling `rawData()`
 * and catching — is worse than it looks: `ResultSet.js:906-911` throws
 * *"Method is not supported for a 'compareDateRangeQuery' query type. Please use
 * decompose"*, and `getWidgetData` SWALLOWS that throw (`catch -> return null`),
 * which the renderer shows as a permanent Skeleton with no visible error. So the
 * only safe detection is structural, up front, on the query we ourselves built.
 */
const isCompareQuery = ( cubeQuery: Query ): boolean =>
  Array.isArray( cubeQuery.timeDimensions ) &&
  cubeQuery.timeDimensions.some(
    ( timeDimension ) =>
      typeof timeDimension === 'object' &&
      timeDimension !== null &&
      'compareDateRange' in timeDimension
  );

/**
 * Split a compare ResultSet into `[ current, prior ]`.
 *
 * **The ordering is load-bearing, and it is guaranteed by construction, not by
 * luck.** The api-gateway's `compareDateRangeTransformer` fans the query out with
 * `queryCompareDateRange.map( dateRange => ... )` — one sub-query per entry, in
 * ARRAY ORDER — and those become `results[]` in that same order. The client's
 * `decompose()` is `this.loadResponses.map(...)`, i.e. `results.map(...)`, which
 * preserves it. So `compareDateRange[i]` <-> `decompose()[i]`, and because
 * `builderDateRangeToTimeDimension` emits `[ window, prior ]`, index 0 is the
 * CURRENT period and index 1 is the PRIOR one.
 *
 * A missing prior ResultSet yields `prior: null` (unknown), never `[]` (zero).
 */
const decomposeCompare = ( resultSet: ResultSet ): CompareWidgetData => {
  const [ current, prior ] = resultSet.decompose().map(( part ) => part.rawData() as CubeRow[]);

  return {
    current: current ?? [],
    prior: prior ?? null
  };
};

/**
 * What a `table` widget is handed instead of `rawData()`: the pivoted rows plus
 * the column annotation they are keyed by. Both come from the same
 * `pivotConfig`, so they always line up.
 *
 * Note there is no `totals` here. `resultSet.totalRow()` LOOKS like the right
 * source for one, but it is implemented as `chartPivot(pivotConfig)[0]` — it
 * returns the FIRST pivoted row, not an aggregate. On a grouped query that is a
 * real number attached to a false label ("Total: $3,000" when $3,000 is merely
 * the top row). Totals are therefore computed on the display side, over the rows
 * actually rendered — see `WebWidget/tableData.computeTotals`.
 */
export type TableWidgetData = {
  rows: TableWidgetRow[];
  columns: TableColumn[];
};

/** `true` for the payload shape produced by the `table` branch below. */
export const isTableWidgetData = ( data: unknown ): data is TableWidgetData =>
  typeof data === 'object' &&
  data !== null &&
  Array.isArray(( data as TableWidgetData ).rows ) &&
  Array.isArray(( data as TableWidgetData ).columns );

/**
 * Read a `PivotConfig` out of the free-form `widgetComponentProps`.
 *
 * `undefined` (rather than `{}`) when nothing usable is configured, so Cube
 * applies its own default normalization — dimensions on `x`, `measures` on `y`,
 * i.e. an ordinary flat table. A partial config (`x` only) is legal: Cube's
 * `normalizePivotConfig` fills in the missing members.
 */
export const toPivotConfig = ( raw: unknown ): PivotConfig | undefined => {
  if ( typeof raw !== 'object' || raw === null ) return undefined;

  const { x, y } = raw as { x?: unknown; y?: unknown };
  const config: PivotConfig = {};

  if ( Array.isArray( x ) && x.every(( m ): m is string => typeof m === 'string' )) config.x = x;
  if ( Array.isArray( y ) && y.every(( m ): m is string => typeof m === 'string' )) config.y = y;

  return config.x || config.y ? config : undefined;
};

/**
 * Project a loaded Cube `ResultSet` into the payload the widget's parser wants.
 *
 * Default (and every chart type): `rawData()`, exactly as before.
 */
export const extractWidgetData = (
  definition: WidgetDefinition,
  cubeQuery: Query,
  resultSet: ResultSet
): unknown => {
  // TV1-1930 — compare is checked BEFORE the type switch, and that order is a
  // hard constraint, not a preference: on a `compareDateRangeQuery` the ResultSet
  // THROWS from `rawData()`, `tablePivot()` and `annotation()` alike. `decompose()`
  // is the only legal way in. Blast radius on the six chart types + `table`: ZERO
  // — none of them can set `compareDateRange` (the builder only offers the toggle
  // for `kpi`), so `isCompareQuery` is always false for them and their path is
  // byte-identical to before.
  if ( isCompareQuery( cubeQuery )) {
    return decomposeCompare( resultSet );
  }

  switch ( definition.type ) {
    case 'table': {
      const pivotConfig = toPivotConfig( definition.widgetComponentProps?.pivotConfig );

      const data: TableWidgetData = {
        rows: resultSet.tablePivot( pivotConfig ),
        columns: resultSet.tableColumns( pivotConfig )
      };

      return data;
    }
    default:
      return resultSet.rawData();
  }
};

// =============================================================================
// TV1-2291 (§6) — the READMODEL source: the THIRD fetch branch
// =============================================================================

/**
 * The readmodel data binding, as it survives the domain -> builder adapter.
 *
 * Structural on purpose. `WidgetDefinition` already declares `collection` and
 * `filter` (they are edited by the inspector), but this function must also accept
 * a definition straight out of `JSON.parse` — a persisted layout is untyped by
 * construction — so `filter` is `unknown[]` here and every clause is re-checked by
 * {@link isReadmodelFilter} before it is planned. That check is the K11 seam; see
 * {@link toReadmodelQuery}.
 */
export type ReadmodelWidgetDefinition = {
  type?: string;
  collection?: string;
  filter?: unknown[];
  limit?: number;
};

/**
 * What a readmodel-backed widget is handed instead of a Cube `ResultSet` projection.
 *
 * ⚠️ TRUTHY by construction, like {@link DATALESS_WIDGET_DATA} — `WebWidget` opens
 * with `if ( !data ) return <Skeleton/>`, so an empty readmodel must still arrive as
 * an object. `{ rows: [], count: 0 }` renders the widget's own empty state; `null`
 * would render a spinner that never resolves.
 *
 * `rows` are the rows the widget may render — already client-filtered and truncated
 * where the plan says so. `count` is the SERVER's total on a `-search` collection
 * (unbounded by `limit`, which is what a `count` tile actually wants) and
 * `rows.length` on a base route, which has no `count` to give — see
 * `readmodelSource.readReadmodelCount`.
 */
export type ReadmodelWidgetData = {
  rows: ReadmodelRow[];
  count: number;
};

/** `true` for the payload shape produced by the readmodel branch below. */
export const isReadmodelWidgetData = ( data: unknown ): data is ReadmodelWidgetData =>
  typeof data === 'object' &&
  data !== null &&
  Array.isArray(( data as ReadmodelWidgetData ).rows ) &&
  typeof ( data as ReadmodelWidgetData ).count === 'number';

/**
 * Every generated client-library operation a {@link ReadmodelQueryPlan} can name —
 * the `baseOperation` and `searchOperation` of every entry in `READMODEL_CATALOG`.
 *
 * ⚠️ This list exists so the dispatch below is a LOOKUP, never a raw `fetch` and
 * never a dynamic property access on an untyped object. `countOperation` is
 * deliberately absent: `planReadmodelQuery` never names it (a count route returns
 * `{ count }` with no rows, which is a different plan shape), so listing it here
 * would advertise a dispatch that cannot happen.
 */
export const READMODEL_OPERATION_NAMES = [
  'ItemsPublishingPublications',
  'ItemsPublishingPublicationsSearch',
  'ItemsPublishingInventory',
  'ItemsPublishingInventorySearch',
  'BatchProcesses',
  'CrmQuestions',
  'SalesOrders',
  'SalesOrdersSearch',
  'ItemsPublishingPublicationDriftEpisodes',
  'ItemsPublishingPublicationDriftEpisodesSearch',
  'RuleExecutions',
  'RuleExecutionsSearch',
  'ItemsPublishingSizeGuides',
  'ItemsPublishingSizeGuidesSearch',
  'Notifications'
] as const;

export type ReadmodelOperationName = typeof READMODEL_OPERATION_NAMES[number];

export const isReadmodelOperationName = ( name: string ): name is ReadmodelOperationName =>
  ( READMODEL_OPERATION_NAMES as readonly string[]).includes( name );

/** The only query params a readmodel widget ever sends. No `offset` — pv3 drops it. */
export type ReadmodelQueryParams = {
  filter?: ReadmodelFilter[];
  limit?: number;
};

/**
 * The shape every generated readmodel query operation has:
 * `( params?, headers? ) => Promise<unknown>`
 * (`teselly-client-library/src/base/SSE.ts` — `ReadmodelQueryBase<Model>` is the
 * same `{ limit, offset, after, sort, filter, select, byDistinctField }` for all of
 * them; only the row/wrapper TYPE differs, and that difference is what the catalog's
 * `responseKey` exists to resolve).
 */
export type ReadmodelQueryOperation = (
  params?: ReadmodelQueryParams,
  headers?: Record<string, string | undefined>
) => Promise<unknown>;

/** The operations {@link fetchReadmodelWidgetData} dispatches over, by name. */
export type ReadmodelQueryDispatchTable = Record<ReadmodelOperationName, ReadmodelQueryOperation>;

/**
 * THE RENAME GATE. `Pick` is constrained by `K extends keyof T`, so a name in
 * {@link READMODEL_OPERATION_NAMES} that the generated client library does not
 * export is a **compile error on this line** — not a runtime "operation is not a
 * function" inside a widget nobody is watching.
 */
export type ReadmodelQuerySurface = Pick<ClientLibraryType['queries'], ReadmodelOperationName>;

/**
 * Narrow the generated query surface to the dispatch table.
 *
 * This one assignment is the whole type story of the branch, and it is checked by
 * the compiler rather than asserted: it only holds if every operation named above
 * really does accept `{ filter, limit }` and really does return a promise. Call it
 * at the boundary (`backend.queries`) — everything downstream then works against
 * `ReadmodelQueryDispatchTable`, which a test can build without reproducing the
 * client library's row types.
 */
export const asReadmodelQueryDispatchTable = (
  queries: ReadmodelQuerySurface
): ReadmodelQueryDispatchTable => queries;

/**
 * Runtime overrides as `WidgetLayoutComponent` passes them (`WidgetFilterOverrides`).
 * Structural rather than imported, to keep this module free of a cycle back into the
 * component — and deliberately `unknown[]`, because the whole point of K11 below is
 * that these clauses are in a DIFFERENT grammar and must not be trusted.
 */
export type ReadmodelFilterOverrides = {
  dateRange?: unknown;
  filters?: unknown[];
};

/**
 * Build the readmodel query for one widget, folding in the dashboard's runtime
 * overrides.
 *
 * ## K11 — a Cube-shaped override is DROPPED, never translated
 *
 * Cube speaks `{ member, operator, values }`; readmodels speak `{ field, op, value }`
 * over a different, 16-strong operator set. There is no honest mapping (`inDateRange`
 * has no readmodel counterpart; `equals` over a multi-value `values[]` is an `in`,
 * not an `eq`), so the branch REFUSES rather than guesses: a readmodel widget under
 * an active dashboard period filter renders **unfiltered**, which is visibly the
 * whole dataset, instead of **wrongly filtered**, which is a plausible number that is
 * not the answer to any question.
 *
 * The refusal is not a bespoke check. It is `readmodelSource.isReadmodelFilter` — the
 * exact predicate ticket 22 built as this seam — used as the array filter's type
 * guard, so `unknown[]` narrows to `ReadmodelFilter[]` and a clause that is not in the
 * readmodel grammar simply does not survive the narrowing. `planReadmodelQuery`
 * applies the same guard again on the way through; the two are idempotent.
 *
 * `overrides.dateRange` has no readmodel expression at all and is not consulted: a
 * readmodel row has no declared time dimension for the toolbar to rebind, and
 * inventing one (`createdAt`? `updatedAt`? `receivedAt`?) would silently narrow a
 * count to a window its author never asked for.
 */
export const toReadmodelQuery = (
  definition: ReadmodelWidgetDefinition,
  overrides?: ReadmodelFilterOverrides
): ReadmodelQuery => {
  const collection = typeof definition.collection === 'string' ? definition.collection.trim() : '';

  if ( !collection ) {
    throw new Error(
      `A "${ definition.type ?? 'readmodel' }" widget must name a readmodel collection. `
      + '`collection` is REQUIRED by UiWidgetReadmodelBaseSchema; a widget without one '
      + 'has nothing to fetch and would render a confident 0 forever.'
    );
  }

  const clauses: unknown[] = [
    ...( definition.filter ?? []),
    // K11 — the runtime overrides are Cube-grammar and are expected to fail the
    // guard below. They are concatenated rather than skipped so the seam stays a
    // single predicate: if the toolbar ever learns to emit readmodel clauses, they
    // pass the same check and start applying, with no new branch here.
    ...( overrides?.filters ?? [])
  ];

  return {
    collection,
    filter: clauses.filter( isReadmodelFilter ),
    ...( typeof definition.limit === 'number' ? { limit: definition.limit } : {})
  };
};

/** The params a plan sends to the server. Empty keys are OMITTED, never sent as `[]`. */
export const toReadmodelQueryParams = ( plan: ReadmodelQueryPlan ): ReadmodelQueryParams => ({
  ...( plan.serverFilter.length ? { filter: plan.serverFilter } : {}),
  ...( plan.limit === undefined ? {} : { limit: plan.limit })
});

/**
 * Fetch ONE readmodel-backed widget's data.
 *
 * The whole branch, in five steps, each of them ticket 22's:
 *
 *  1. `toReadmodelQuery` — the stored binding + the K11-filtered overrides;
 *  2. `planReadmodelQuery` — which route, which half of the filter goes where;
 *  3. the generated client library, by the plan's operation name — **never `fetch`**;
 *  4. `unwrapReadmodelResponse` — by the catalog's DECLARED key, and it THROWS on a
 *     missing one (five distinct keys across the catalog: `pagePublications`,
 *     `pageItems`, `pageOrders`, `episodes`, `items` — a guess is an empty widget
 *     with no error anywhere);
 *  5. `applyPlanToRows` — the client-side half, filter FIRST then truncate.
 *
 * Every throw on that path — an unknown collection, a missing wrapper key, an HTTP
 * failure — propagates to `getWidgetData`'s `catch`, which turns it into
 * `{ status: 'error' }` and an error TILE. Never a permanent skeleton: that
 * regression is what `WidgetDataState` exists to prevent.
 */
export const fetchReadmodelWidgetData = async (
  definition: ReadmodelWidgetDefinition,
  queries: ReadmodelQueryDispatchTable,
  overrides?: ReadmodelFilterOverrides
): Promise<ReadmodelWidgetData> => {
  const plan = planReadmodelQuery( toReadmodelQuery( definition, overrides ));

  if ( !isReadmodelOperationName( plan.operation )) {
    throw new Error(
      `Readmodel collection "${ plan.collection.collection }" names client-library `
      + `operation "${ plan.operation }", which this build cannot dispatch. `
      + `Known operations: ${ READMODEL_OPERATION_NAMES.join( ', ' ) }.`
    );
  }

  const response = await queries[plan.operation]( toReadmodelQueryParams( plan ));
  const rows = applyPlanToRows( plan, unwrapReadmodelResponse( plan, response ));

  return { rows, count: readReadmodelCount( plan, response, rows ) };
};
